import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  PacketDetailMouseEvent,
  PacketDetailNextButtonClickEvent,
  PacketDetailPreviousButtonClickEvent,
} from 'components/DeviceView/DeviceViewContent/PacketDetail/PacketDetail';
import type { PacketDetailCloseEvent } from 'components/DeviceView/DeviceViewContent/PacketDetail/PacketDetailCloseButton';
import type { PathInspectorPacketSelectEvent } from 'components/DeviceView/DeviceViewContent/PathInspector/PathInspectorPacketDetail';
import type { SelectPacketMouseEvent } from 'components/DeviceView/DeviceViewContent/PathInspector/PathInspectorSelectPacketFromList';
import type { DeviceViewEntityTabMouseEvent } from 'components/DeviceView/DeviceViewHeader';
import { PATH_INSPECTOR_DETAIL_TAB } from 'components/DeviceView/deviceViewConstants';
import type { CloseDeviceViewEntityEvent } from 'components/DeviceView/deviceViewTypes';
import type { DeviceViewModel } from 'components/DeviceView/model/DeviceViewModel';
import type { HandlersContainer } from 'components/DeviceView/model/HandlersContainer';
import type { SelectEntityTabCommand } from 'components/DeviceView/model/commands/selectEntityTab';
import { useDeviceView } from 'components/DeviceView/useDeviceView';
import type { GraphState } from 'components/Graph/GraphState';
import { highlightEdge } from 'components/Graph/commands/edges/highlightEdge';
import { unhighlightEdge } from 'components/Graph/commands/edges/unhighlightEdge';
import {
  type ChangePathLookupPathInspectorActiveNodeCommand,
  changePathLookupPathInspectorActiveNode,
} from 'components/Graph/commands/pathLookup/changePathLookupPathInspectorActiveNode';
import { pathLookupDetailHover } from 'components/Graph/commands/pathLookup/pathLookupDetailHover';
import { pathLookupDetailLeave } from 'components/Graph/commands/pathLookup/pathLookupDetailLeave';
import { selectPathInspectorPacket } from 'components/Graph/commands/pathLookup/selectPathInspectorPacket';
import { submitTechnologies } from 'components/Graph/commands/topology/submitTechnologies';
import { updateStpInstance } from 'components/Graph/commands/updateStpInstance';
import { updateStpInstanceIsolate } from 'components/Graph/commands/updateStpInstanceIsolate';
import type { GraphCommandContext } from 'components/Graph/model/GraphCommandContext';
import type {
  StpInstanceIsolateEvent,
  StpInstanceUpdateEvent,
} from 'components/STPInstanceMembers/STPInstanceMembers';
import { type RefObject, useMemo } from 'react';
import type { CommandDispatcher } from 'utils/react/useCommandDispatcher';
import { closeDeviceViewEntity } from './commands/deviceView/closeDeviceViewEntity';
import { highlightPathLookupPathInspectorElement } from './commands/deviceView/highlightPathLookupPathInspectorElement';
import { pathLookupDetailNextButtonClick } from './commands/deviceView/pathLookupDetailNextButtonClick';
import { pathLookupDetailNextButtonMouseEnter } from './commands/deviceView/pathLookupDetailNextButtonMouseEnter';
import { pathLookupDetailNextButtonMouseLeave } from './commands/deviceView/pathLookupDetailNextButtonMouseLeave';
import { pathLookupDetailPreviousButtonClick } from './commands/deviceView/pathLookupDetailPreviousButtonClick';
import { pathLookupDetailPreviousButtonMouseEnter } from './commands/deviceView/pathLookupDetailPreviousButtonMouseEnter';
import { pathLookupDetailPreviousButtonMouseLeave } from './commands/deviceView/pathLookupDetailPreviousButtonMouseLeave';
import { unhighlightPathLookupPathInspectorElement } from './commands/deviceView/unhighlightPathLookupPathInspectorElement';
import { clearSelectedHighlightedPathLookupEdge } from './commands/pathLookup/clearSelectedHighlightedPathLookupEdge';
import { destroyPathLookupPathInspector } from './commands/pathLookup/destroyPathLookupPathInspector';
import { hideHighlightedEdges } from './commands/pathLookup/hideHighlightedEdges';
import { showHighlightedEdges } from './commands/pathLookup/showHighlightedEdges';
import { EDGE_HIGHLIGHT_THICKNESS } from './constants';

interface Props {
  deviceViewContainer: RefObject<HTMLDivElement>;
  dispatch: CommandDispatcher<GraphCommandContext>;
  graph: GraphState;
}

/**
 * Do not pass command to deviceView hook!!!!!
 * Use event handler. Commands don't exist when deviceView is being initialised.
 */
export const useGraphDeviceView = (props: Props): DeviceViewModel => {
  const handleCloseDeviceExplorer = useStableCallback(() => {
    props.dispatch(destroyPathLookupPathInspector);
  });

  const handleCloseDeviceViewEntity = useStableCallback(
    (event: CloseDeviceViewEntityEvent) => {
      props.dispatch(closeDeviceViewEntity, event);
    },
  );

  const handleStpInstanceUpdate = useStableCallback(
    (event: StpInstanceUpdateEvent) => {
      props.dispatch(updateStpInstance, event);
      props.dispatch(submitTechnologies);
    },
  );

  const handleStpInstanceIsolate = useStableCallback(
    (event: StpInstanceIsolateEvent) => {
      props.dispatch(updateStpInstanceIsolate, event);
      props.dispatch(submitTechnologies);
    },
  );

  const handleHighlightPathLookupPathInspectorNode = useStableCallback(
    (event: DeviceViewEntityTabMouseEvent) => {
      props.dispatch(highlightPathLookupPathInspectorElement, event);
    },
  );

  const handleUnhighlightPathLookupPathInspectorNode = useStableCallback(
    (event: DeviceViewEntityTabMouseEvent) => {
      props.dispatch(unhighlightPathLookupPathInspectorElement, event);
    },
  );

  const handleSelectPacketHover = useStableCallback(
    (event: SelectPacketMouseEvent) => {
      props.dispatch(highlightEdge, {
        edgeId: event.edgeId,
        thickness: EDGE_HIGHLIGHT_THICKNESS,
      });
    },
  );

  const handleSelectPacketLeave = useStableCallback(
    (event: SelectPacketMouseEvent) => {
      props.dispatch(unhighlightEdge, {
        edgeId: event.edgeId,
        thickness: true,
      });
    },
  );

  const changePathInspectorActiveNode = useStableCallback(
    (command: ChangePathLookupPathInspectorActiveNodeCommand) => {
      props.dispatch(changePathLookupPathInspectorActiveNode, command);
    },
  );

  const handlePathInspectorPacketSelect = useStableCallback(
    (event: PathInspectorPacketSelectEvent) => {
      props.dispatch(selectPathInspectorPacket, event);
    },
  );

  const handlePacketDetailHover = useStableCallback(
    (event: PacketDetailMouseEvent) => {
      props.dispatch(pathLookupDetailHover, event);
    },
  );

  const handlePacketDetailLeave = useStableCallback(
    (event: PacketDetailMouseEvent) => {
      props.dispatch(pathLookupDetailLeave, event);
    },
  );

  const handleRemovePacketDetail = useStableCallback(
    (event: PacketDetailCloseEvent) => {
      props.dispatch(clearSelectedHighlightedPathLookupEdge, event);
    },
  );

  const handlePacketDetailNextButtonClick = useStableCallback(
    (event: PacketDetailNextButtonClickEvent) => {
      props.dispatch(pathLookupDetailNextButtonClick, event);
    },
  );

  const handlePacketDetailNextButtonMouseEnter = useStableCallback(
    (event: PacketDetailMouseEvent) => {
      props.dispatch(pathLookupDetailNextButtonMouseEnter, event);
    },
  );

  const handlePacketDetailNextButtonMouseLeave = useStableCallback(
    (event: PacketDetailMouseEvent) => {
      props.dispatch(pathLookupDetailNextButtonMouseLeave, event);
    },
  );

  const handlePacketDetailPreviousButtonClick = useStableCallback(
    (event: PacketDetailPreviousButtonClickEvent) => {
      props.dispatch(pathLookupDetailPreviousButtonClick, event);
    },
  );

  const handlePacketDetailPreviousButtonMouseEnter = useStableCallback(
    (event: PacketDetailMouseEvent) => {
      props.dispatch(pathLookupDetailPreviousButtonMouseEnter, event);
    },
  );

  const handlePacketDetailPreviousButtonMouseLeave = useStableCallback(
    (event: PacketDetailMouseEvent) => {
      props.dispatch(pathLookupDetailPreviousButtonMouseLeave, event);
    },
  );

  const handleContentEntityTabChange = useStableCallback(
    (event: SelectEntityTabCommand) => {
      if (event.tabId === PATH_INSPECTOR_DETAIL_TAB) {
        props.dispatch(showHighlightedEdges);
      } else {
        props.dispatch(hideHighlightedEdges);
      }
    },
  );

  const handlersContainer = useMemo(
    (): HandlersContainer => ({
      handleCloseDeviceExplorer,
      handleCloseDeviceViewEntity,
      handleActiveDeviceEntityChange: changePathInspectorActiveNode,
      handleBarActivate: changePathInspectorActiveNode,
      handleDeviceViewEntityTabMouseEnter:
        handleHighlightPathLookupPathInspectorNode,
      handleDeviceViewEntityTabMouseLeave:
        handleUnhighlightPathLookupPathInspectorNode,
      handlePathInspectorPacketSelect,
      handlePacketDetailHover,
      handlePacketDetailLeave,
      handlePacketDetailNextButtonClick,
      handlePacketDetailNextButtonMouseEnter,
      handlePacketDetailNextButtonMouseLeave,
      handlePacketDetailPreviousButtonClick,
      handlePacketDetailPreviousButtonMouseEnter,
      handlePacketDetailPreviousButtonMouseLeave,
      handleSelectPacketHover,
      handleSelectPacketLeave,
      handleRemovePacketDetail,
      handleStpInstanceIsolate,
      handleStpInstanceUpdate,
      handleWindowActivate: changePathInspectorActiveNode,
      handleWindowMinimize: changePathInspectorActiveNode,
      handleContentEntityTabChange,
    }),
    [],
  );

  return useDeviceView({
    container: props.deviceViewContainer.current,
    handlersContainer,
  });
};
